import { Component as ReactComponent, ReactNode, Fragment } from 'react'
import ReactDOM from 'react-dom'
import { parse } from 'querystringify'
import { connect } from 'react-redux'
import cn from 'clsx'
import { motion, AnimatePresence } from 'framer-motion'

// Component
import { Div } from 'components/staticpages/blocks/frontpage/mediaandtext2021'

// Utils
import { isBrowser } from '@process'

// Types
import { State } from 'types'

import styles from './modal.css'

type ModalSizeType = 'SMALL' | 'MEDIUM' | 'LARGE' | 'XXL'

const getSizeClassName = (size: ModalSizeType) => {
  switch (size) {
    case 'SMALL':
      return styles.sizeSmall
    case 'LARGE':
      return styles.sizeLarge
    case 'XXL':
      return styles.sizeXXL
    default:
      return styles.sizeMedium
  }
}

type ModalProps = {
  children: ReactNode
  modalTitle?: string
  modalSize?: ModalSizeType
  className?: string
  backdropClassName?: string
  isOpen: boolean
  location: {
    search: string
  }
  closeOnBackdropClick?: boolean
  transitionEnabled?: boolean
  closeOnEscKey?: boolean
  onClose?: () => void
  fullscreen?: boolean
  noPadding?: boolean
  mobileFullscreen?: boolean
  mobileAlignment?: string
  transitionDuration?: number
}

class Modal extends ReactComponent<ModalProps> {
  focusedElementBeforeModal: HTMLElement | null | undefined

  static defaultProps = {
    closeOnBackdropClick: true,
    closeOnEscKey: true,
    transitionEnabled: true,
  }

  constructor(props: ModalProps) {
    super(props)
    if (isBrowser) {
      this.modalElement = document.createElement('div')
      if (document.body) {
        document.body.appendChild(this.modalElement)
      }
      this.focusedElementBeforeModal = null
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: ModalProps) {
    if (nextProps.isOpen) {
      // save which element has focus when opening, but don't do it when moving between modals
      if (!(this.props.isOpen && nextProps.isOpen)) {
        this.focusedElementBeforeModal = document.activeElement
      }
      if (typeof window !== 'undefined') {
        window.addEventListener('keydown', this.handleKeyDown)
      }
    } else if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleKeyDown)
    }
  }

  componentDidUpdate(prevProps: ModalProps) {
    const { isOpen } = this.props
    if (isOpen && !prevProps.isOpen) {
      const firstTabStop = this.getFocusableElements()[0]
      if (firstTabStop) {
        firstTabStop.focus()
      }
    }
  }

  componentWillUnmount() {
    if (document.body) {
      document.body.removeChild(this.modalElement)
    }
  }

  getFocusableElements() {
    const focusableElementsString =
      'a[href], area[href], input:not([type="hidden"]):not([disabled]), select:not([type="hidden"]):not([disabled]), textarea:not([type="hidden"]):not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]'
    let focusableElements = this.modalElement.querySelectorAll(focusableElementsString)
    focusableElements = Array.prototype.slice.call(focusableElements)
    return focusableElements.filter(element => element.focus)
  }

  modalElement: HTMLElement

  handleKeyDown = (event: KeyboardEvent) => {
    const { closeOnEscKey } = this.props

    // Check for TAB key press
    if (event.keyCode === 9) {
      const firstTabStop = this.getFocusableElements()[0]
      const lastTabStop = this.getFocusableElements()[this.getFocusableElements().length - 1]

      // SHIFT + TAB
      if (event.shiftKey) {
        if (firstTabStop && document.activeElement === firstTabStop) {
          event.preventDefault()
          lastTabStop.focus()
        }
      } else if (firstTabStop && document.activeElement === lastTabStop) {
        // TAB
        event.preventDefault()
        firstTabStop.focus()
      }
    }

    if (event.keyCode === 27 && closeOnEscKey) {
      this.close()
    }
  }

  handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const { closeOnBackdropClick } = this.props
    if (event.target && event.target.dataset && event.target.dataset.modal && closeOnBackdropClick) {
      this.close()
    }
  }

  close = () => {
    const { onClose } = this.props
    if (onClose) {
      onClose()
    }
    if (this.focusedElementBeforeModal) {
      this.focusedElementBeforeModal.focus()
    }
  }

  renderModal = () => {
    const {
      modalTitle,
      modalSize,
      isOpen,
      transitionEnabled,
      children,
      className,
      fullscreen,
      mobileFullscreen,
      mobileAlignment,
      transitionDuration,
      location,
      noPadding,
    } = this.props
    const searchQuery = parse(location.search) as { full: string }
    const isFullscreen = searchQuery.full === 'true' || fullscreen
    const transition = { duration: transitionDuration || 0.3 }

    const Component = transitionEnabled ? motion.div : Div

    /* onClick below used for "backdrop" clicks */
    return (
      <Component
        className={cn(
          styles.wrapper,
          className,
          isFullscreen && styles.fullscreen,
          mobileFullscreen && styles.mobileFullscreen,
          !transitionEnabled && isOpen && styles.isOpen,
        )}
        data-modal="true"
        onMouseDown={this.handleClick}
        onTouchStart={this.handleClick}
        transitionDuration={transitionDuration}
        key="modaldiv"
        initial={{ opacity: 0, transition }}
        exit={{ opacity: 0, transition }}
        animate={{ opacity: 1, transition }}
      >
        <div className={cn(styles.content, getSizeClassName(modalSize), noPadding && styles.noPadding)}>
          {modalTitle && (
            <div className={styles.titleWrapper}>
              <h3>{modalTitle}</h3>
            </div>
          )}
          <div className={mobileAlignment === 'TOP' ? styles.bodyTopAlignment : styles.body}>{children}</div>
        </div>
      </Component>
    )
  }

  renderBackdrop = () => {
    const { isOpen, transitionEnabled, backdropClassName, transitionDuration } = this.props
    const transition = { duration: transitionDuration || 0.3 }
    const Component = transitionEnabled ? motion.div : Div
    return (
      <Component
        key="backdropdiv"
        transitionDuration={transitionDuration}
        className={cn(styles.backdrop, !transitionEnabled && isOpen && styles.isOpen, backdropClassName)}
        initial={{ opacity: 0, transition }}
        exit={{ opacity: 0, transition }}
        animate={{ opacity: 1, transition }}
      />
    )
  }

  renderContent = () => {
    const { isOpen, transitionEnabled } = this.props
    const Component = transitionEnabled ? AnimatePresence : Fragment
    return (
      <Component>
        {isOpen && this.renderModal()}
        {isOpen && this.renderBackdrop()}
      </Component>
    )
  }

  render() {
    if (isBrowser) {
      return ReactDOM.createPortal(this.renderContent(), this.modalElement)
    }
    return null
  }
}

export { Modal as Component }

const mapStateToProps = (state: State) => ({
  location: state.router.location,
})
export default connect(mapStateToProps)(Modal)
